/**
 * Dependencies
 */
import {
    removeInvite,
    updatePrivilege,
    getInvitationList,
    acceptProjectInvite,
    rejectProjectInvite,
    getSentInvitationList,
    getConfidentialityAgreement,
    acceptAgreementAndInvitation
} from '../../../../services/InvitationService'

/**
 * Get the invite list from the API
 *
 * @param  {Function} options.commit
 * @return {Promise}
 */
const getInvitations = ({ commit }) => {
    commit('SET_PENDING')
    return Promise
        .all([
            getInvitationList(),
            getSentInvitationList()
        ])
        .then(res => {
            commit('GET_INVITES_SUCCESS', res[0].data)
            commit('GET_SENT_INVITES_SUCCESS', res[1].data)
        })
        .catch(err => Promise.reject(err))
}

/**
 * Send request to accept invitation by ID
 *
 * @param  {Function} options.commit
 * @param  {Number} id
 * @return {Promise}
 */
const acceptInvite = ({ commit, dispatch }, id) => {
    return acceptProjectInvite(id)
        .then(res => {
            commit('ACCEPT_INVITE', id)
            dispatch('addNotification', {
                title: 'Invitation Accepted',
                type: 'success',
                text: 'The invitation was accepted and the project is now available on your dashboard.',
                timeout: true
            }, { root: true })
            Promise.resolve(id)
        })
        .catch(err => console.log(err))
}

/**
 * Send request to reject invitation by ID
 *
 * @param  {Function} options.commit
 * @param  {Number} id
 * @return {Promise}
 */
const rejectInvite = ({ commit }, id) => {
    if (window.confirm('Are you sure you want to reject this invitation?')) {
        return rejectProjectInvite(id)
            .then(res => commit('REJECT_INVITE', id))
            .catch(err => console.log(err))
    }
}

/**
 * Trigger the state reset
 *
 * @param  {Function} options.commit
 * @return {Void}
 */
const resetState = ({ commit }) => {
    commit('RESET_STATE')
}

/**
 * Update a privilege level on a given invitation
 *
 * @param  {Function} options.commit
 * @return {Void}
 */
const updatePrivilegeLevel = ({ commit, dispatch }, payload) => {
    updatePrivilege(payload)
        .then(res => commit('UPDATE_PRIVILEGE_LEVEL', payload))
        .then(() => dispatch('addNotification', {
            title: 'Invitation updated',
            type: 'success',
            text: 'The user\'s invitation level has been updated successfully.',
            timeout: true
        }, { root: true }))
        .catch(err => console.log(err))
}

/**
 * Delete a sent invitation
 *
 * @param  {Function} options.commit
 * @param  {Number} id
 * @return {Promise}
 */
const removeSentInvite = ({ commit }, id) => {
    if (window.confirm('Do you want to remove this invitation?')) {
        return removeInvite(id)
            .then(res => {
                commit('REMOVE_INVITE', id)
                return res.data
            })
            .catch(err => {
                return err
            })
    }
}

/**
 * Trigger the confidentiality agreement modal
 *
 * @param  {Function} options.commit
 * @return {Promise}
 */
const launchAgreementDialog = ({ commit }, id) => {
    return getConfidentialityAgreement(id)
        .then(res => {
            commit('SHOW_AGREEMENT_MODAL', {
                id: id,
                conf_agreement: res.data.conf_agreement
            })
            Promise.resolve(res.data)
        })
        .catch(err => console.log(err))
}

/**
 * Tear down the agreement modal
 *
 * @param  {Function} options.commit
 * @return {Promise}
 */
const tearDownAgreementDialog = ({ commit }) => {
    commit('CLOSE_AGREEMENT_MODAL')
}

/**
 * Reject the CA and invite
 *
 * @param  {Function} options.commit
 * @return {Void}
 */
const acceptAgreement = ({ commit, state, dispatch }) => {
    let id = state.agreementModal.id
    acceptAgreementAndInvitation(id)
        .then(res => {
            commit('ACCEPT_INVITE', id)
            commit('ACCEPT_AGREEMENT_AND_INVITE')
        })
}

/**
 * Accept the CA and invite
 *
 * @param  {Function} options.commit
 * @return {Void}
 */
const rejectAgreement = ({ commit, dispatch, state }) => {
    let id = state.agreementModal.id
    dispatch('rejectInvite', id)
        .then(res => {
            commit('CLOSE_AGREEMENT_MODAL')
        })
}

/**
 * Update the agreement status
 *
 * @param  {Function} options.commit
 * @return {Void}
 */
const changeAgreementStatus = ({ commit }, checked) => {
    commit('UPDATE_AGREEMENT_VALUE', checked)
}

export default {
    resetState,
    acceptInvite,
    rejectInvite,
    getInvitations,
    acceptAgreement,
    rejectAgreement,
    removeSentInvite,
    updatePrivilegeLevel,
    launchAgreementDialog,
    tearDownAgreementDialog,
    changeAgreementStatus
}



// WEBPACK FOOTER //
// ./src/state/modules/projects/invitations/actions.js